import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Services.css";
import image1 from "url:../../public/accounting.png";
import image2 from "url:../../public/advisor.png";
import image3 from "url:../../public/audit.png";
import image4 from "url:../../public/taxes.png";
import image5 from "url:../../public/formation.png";
import image6 from "url:../../public/Cost.jpg";

const servicesData = [
  {
    title: "Audit and Assurance",
    description:
      "Our team of experienced professionals conducts thorough audits to ensure the accuracy and transparency of financial records. We help businesses adhere to regulatory requirements and industry standards while identifying areas for improvement.",
    imageUrl: image3,
    icon: "📊",
    stats: { projects: "100+", satisfaction: "98%" },
    benefits: [
      "Regulatory Compliance",
      "Risk Assessment",
      "Financial Transparency",
    ],
  },
  {
    title: "Tax Consulting",
    description:
      "We provide expert guidance on tax planning, compliance, and optimization strategies. Our aim is to help businesses minimize their tax liabilities while staying compliant with ever-changing tax laws.",
    imageUrl: image4,
    icon: "💼",
    stats: { projects: "150+", satisfaction: "96%" },
    benefits: ["Tax Optimization", "Compliance Support", "Strategic Planning"],
  },
  {
    title: "Accounting",
    description:
      "We assist in maintaining accurate and up-to-date accounting records, financial statements, and reports. Our services aid in facilitating better financial management and decision making.",
    imageUrl: image1,
    icon: "📈",
    stats: { projects: "200+", satisfaction: "99%" },
    benefits: ["Financial Reporting", "Record Management", "Decision Support"],
  },
  {
    title: "Cost Control",
    description:
      "At our company, we specialize in helping you tackle the challenge of high food costs head-on. We believe in long-term solutions, not quick fixes like raising prices or cutting corners on quality. Our approach involves a thorough examination of your menu, streamlining operations, analyzing your ingredient expenses, and more.",
    imageUrl: image6,
    icon: "💰",
    stats: { projects: "80+", satisfaction: "97%" },
    benefits: ["Cost Reduction", "Process Optimization", "Profit Maximization"],
  },
  {
    title: "Financial Advisory",
    description:
      "Our financial experts offer strategic advice to help clients make informed decisions related to investments, mergers and acquisitions, capital raising, and other financial matters.",
    imageUrl: image2,
    icon: "🎯",
    stats: { projects: "120+", satisfaction: "95%" },
    benefits: ["Strategic Planning", "Investment Guidance", "M&A Support"],
  },
  {
    title: "Company Formation",
    description:
      "Assisting clients in the process of establishing new companies, from legal and regulatory compliance to structural setup.",
    imageUrl: image5,
    icon: "🏢",
    stats: { projects: "90+", satisfaction: "98%" },
    benefits: ["Legal Setup", "Regulatory Compliance", "Business Structure"],
  },
];

function Services() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredServices, setFilteredServices] = useState(servicesData);
  const [selectedService, setSelectedService] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    document.body.style.backgroundColor = "grey";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    const filtered = servicesData.filter(
      (service) =>
        service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredServices(filtered);
    setVisibleCards([]);
    setSelectedService(null);
  }, [searchTerm]);

  useEffect(() => {
    const observers = [];

    cardRefs.current.forEach((card, index) => {
      if (!card) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                setVisibleCards((prev) => [...new Set([...prev, index])]);
              }, 100);
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.2,
          rootMargin: "0px 0px -100px 0px",
        },
      );

      observer.observe(card);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [filteredServices]);

  const handleServiceClick = (index) => {
    setSelectedService(selectedService === index ? null : index);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const handleGetQuote = (e, serviceTitle) => {
    e.stopPropagation();
    navigate("/contact-information", { state: { service: serviceTitle } });
  };

  return (
    <div className="services-container">
      <div className="services-header">
        <h1 className="services-title">Our Services</h1>
        <p className="services-subtitle">
          Comprehensive financial solutions tailored to your needs
        </p>
        <div className="stats-banner">
          <div className="stat-item">
            <span className="stat-number">{servicesData.length}</span>
            <span className="stat-label">Services Offered</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">750+</span>
            <span className="stat-label">Projects Completed</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">97%</span>
            <span className="stat-label">Client Satisfaction</span>
          </div>
        </div>
      </div>

      <div className="search-container">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="🔍 Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={clearSearch}>
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results-info">
            Found {filteredServices.length}{" "}
            {filteredServices.length === 1 ? "service" : "services"}
          </div>
        )}
      </div>

      <div className="services-list">
        {filteredServices.map((service, index) => (
          <div
            ref={(el) => (cardRefs.current[index] = el)}
            className={`service-item ${
              visibleCards.includes(index) ? "visible" : ""
            } ${index % 2 === 0 ? "slide-left" : "slide-right"} ${
              selectedService === index ? "expanded" : ""
            } ${hoveredCard === index ? "hovered" : ""}`}
            key={index}
            onClick={() => handleServiceClick(index)}
            onMouseEnter={() => setHoveredCard(index)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="service-content">
              <div className="service-header-section">
                <div className="service-icon-wrapper">
                  <span className="service-icon">{service.icon}</span>
                </div>
                <div className="service-number">
                  {String(index + 1).padStart(2, "0")}
                </div>
              </div>
              <div className="service-text">
                <h2 className="service-title">
                  {service.title}
                  <span className="expand-indicator">
                    {selectedService === index ? "↑" : "↓"}
                  </span>
                </h2>
                <p className="service-description">{service.description}</p>

                <div
                  className={`service-extra-info ${
                    selectedService === index ? "show" : ""
                  }`}
                >
                  <div className="benefits-list">
                    <h4>Key Benefits:</h4>
                    <ul>
                      {service.benefits.map((benefit, i) => (
                        <li key={i}>
                          <span className="check-icon">✓</span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="info-stats">
                    <div className="info-stat">
                      <span className="info-stat-value">
                        {service.stats.projects}
                      </span>
                      <span className="info-stat-label">Projects</span>
                    </div>
                    <div className="info-stat">
                      <span className="info-stat-value">
                        {service.stats.satisfaction}
                      </span>
                      <span className="info-stat-label">Satisfaction</span>
                    </div>
                  </div>

                  <button
                    className="quote-btn"
                    onClick={(e) => handleGetQuote(e, service.title)}
                  >
                    Request a Quote →
                  </button>
                </div>
              </div>
            </div>
            <div className="service-image-wrapper">
              <img
                src={service.imageUrl}
                alt={service.title}
                className="service-image"
              />
              <div className="image-overlay">
                <span className="overlay-text">Click to learn more</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredServices.length === 0 && (
        <div className="no-results">
          <div className="no-results-icon">🔍</div>
          <h3>No services found</h3>
          <p>Try adjusting your search terms</p>
          <button className="reset-btn" onClick={clearSearch}>
            Clear Search
          </button>
        </div>
      )}
    </div>
  );
}

export default Services;
